import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Alert } from '@mui/material';
import { getSessionId, getCreations, hasRecoveryToken, setRecoveryToken } from '../lib/myCreations';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Plain, single-mechanism email recovery prompt (no "magic link" / QR / copy wording). Shown until an
// email/token has been collected, then it hides itself permanently for this browser. Stacked layout:
// heading → field → button. Used at the top of the create "choose a design" step and on My creations.
const EmailRecoveryPrompt = ({ onCollected }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [message, setMessage] = useState('');
  // Hide as soon as a token exists locally (already collected on a prior visit, or just now).
  const [hidden, setHidden] = useState(() => hasRecoveryToken());

  if (hidden) return null;

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setStatus('sending');
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/creations/email-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: value,
          sessionId: getSessionId(),
          creations: getCreations().map((c) => ({
            cardId: c.cardId,
            coverImageUrl: c.coverImageUrl,
            createdAt: c.createdAt,
          })),
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.success === false) {
        throw new Error((data && data.error) || 'Could not send the link.');
      }
      // A token/flag is stored locally → the prompt is hidden thereafter.
      setRecoveryToken(data.token || '1');
      setStatus('sent');
      setHidden(true);
      if (onCollected) onCollected();
    } catch (err) {
      setStatus('error');
      setMessage('We couldn’t send that link just now. Please try again in a little while.');
    }
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{
        backgroundColor: '#f3f7f8',
        border: '1px solid #dceaee',
        borderRadius: '12px',
        p: { xs: '16px', md: '18px 20px' },
        maxWidth: 460,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
        Want to find your cards again later?
      </Typography>
      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.5, mb: 1.5 }}>
        Enter your email and we’ll send you a link.
      </Typography>
      <TextField
        type="email"
        fullWidth
        size="small"
        placeholder="Your email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        inputProps={{ 'aria-label': 'Your email address' }}
        sx={{ backgroundColor: '#fff', mb: 1.25 }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={status === 'sending' || !email.trim()}
        sx={{
          backgroundColor: '#206884',
          color: '#fff',
          boxShadow: 'none',
          fontWeight: 600,
          py: 1.25,
          '&:hover': { backgroundColor: '#185166', boxShadow: 'none' },
        }}
      >
        {status === 'sending' ? 'Sending…' : 'Email me a link'}
      </Button>
      {status === 'error' && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {message}
        </Alert>
      )}
    </Box>
  );
};

export default EmailRecoveryPrompt;
